import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  Announcement,
  Button,
  ButtonV2,
  Card,
  handlePageClick,
  JoinInnerIcon,
  Loader,
  LoaderSkeleton,
  Pagination,
  Search,
  usePaginatedItems,
} from '@utilities';
import { customGroupStatus } from '@helpers';

import Breakout from './components/Breakout';
import { getFilterBreakouts } from './utilities/helpers';
import styles from './_index.module.scss';

const ItemBreakouts = ({
  groupType,
  item,
  itemsPage,
  itemsRow,
  onSubmit,
  readItem,
  updateItemBreakouts,
}) => {
  const completedStatus = customGroupStatus.COMPLETED;
  const [isItemProcessing, setIsItemProcessing] = useState(item?.status < completedStatus);
  const [isLoading, setIsLoading] = useState(false);
  const [itemBreakouts, setItemBreakouts] = useState(item);
  const [isOverlappingBreakoutsVisible, setIsOverlappingBreakoutsVisible] = useState(false);

  const groups = useSelector((state) => state?.websocket?.customGroups);
  const groupStatusRef = useRef(
    groups?.find((group) => group?.id === item?.id)?.status ?? item?.status
  );

  const { id, isOwner } = itemBreakouts;
  const [value, setValue] = useState('');
  const breakoutsPerPage = 5;

  const navigate = useNavigate();
  const itemControllerRef = useRef(null);

  const getItem = async () => {
    itemControllerRef?.current?.abort();
    const controller = new AbortController();
    itemControllerRef.current = controller;

    try {
      const { data } = await readItem({
        id: item?.id,
        signal: itemControllerRef?.current?.signal,
      });

      setItemBreakouts(data);
      itemControllerRef.current = null;
    } catch (error) {
      if (error?.response?.status > 200) {
        console.error(error);
        setIsLoading(false);
      }
    }
  };

  const filteredBreakouts = useMemo(() => {
    return value
      ? getFilterBreakouts({ breakouts: itemBreakouts?.breakouts, value })
      : itemBreakouts?.breakouts;
  }, [itemBreakouts?.breakouts, value]);

  const overlappingBreakoutTitles = useMemo(() => {
    return new Set(
      Object.entries(itemBreakouts?.overlaps || {})
        .filter(([, overlaps]) => overlaps?.length > 0)
        .map(([title]) => title)
    );
  }, [itemBreakouts?.overlaps]);

  const visibleBreakouts = useMemo(() => {
    if (!isOverlappingBreakoutsVisible) {
      return filteredBreakouts;
    }

    return filteredBreakouts?.filter((breakout) => overlappingBreakoutTitles.has(breakout?.title));
  }, [filteredBreakouts, isOverlappingBreakoutsVisible, overlappingBreakoutTitles]);

  const getOverlapMessage = (breakoutTitle) => {
    const overlappingBreakouts = itemBreakouts?.overlaps?.[breakoutTitle] || [];
    const visibleOverlaps = overlappingBreakouts.slice(0, 10);
    const overlapList = visibleOverlaps.join(', ');

    return overlappingBreakouts.length > 10 ? `${overlapList}, and others.` : `${overlapList}.`;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await getItem();
      setIsLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleGroupStatus = async () => {
      const group = groups?.find((group) => group?.id === item?.id);

      if (group) {
        const previousStatus = Number(groupStatusRef.current);
        const status = Number(group.status);
        groupStatusRef.current = status;

        if (status < completedStatus) {
          setIsItemProcessing(true);
        }
        if (status >= completedStatus) {
          if (previousStatus < completedStatus) await getItem();

          setIsItemProcessing(false);
        }
      }
    };

    handleGroupStatus();
  }, [groups]);

  const { currentItems, itemsPerPage, pageCount, setOffset } = usePaginatedItems({
    items: visibleBreakouts,
    itemsPerPage: breakoutsPerPage,
  });

  const onPageChange = (event) => {
    handlePageClick({ event, items: visibleBreakouts, itemsPerPage: breakoutsPerPage, setOffset });
  };

  const handleNavigate = ({ id }) => {
    navigate(`/dashboard/create-breakout/${id}`, {
      state: { itemsPage, itemsRow, type: groupType?.type },
    });
  };

  if ((isLoading && !isItemProcessing) || !filteredBreakouts) {
    return (
      <div>
        <LoaderSkeleton height={40} width={2000}>
          <rect height="40" rx="2" ry="2" width="100%" x="0" y="0" />
        </LoaderSkeleton>
        {[...Array(currentItems?.length)].map((item, index) => (
          <LoaderSkeleton height={110} key={index} width={2000}>
            <rect height="110" rx="2" ry="2" width="100%" x="0" y="0" />
          </LoaderSkeleton>
        ))}
      </div>
    );
  }

  const renderOverlappingBreakouts = () => {
    return (
      <ButtonV2
        className={styles['item-breakouts-overlapping-breakouts-button']}
        data-testid="item-breakouts-overlap-toggle"
        icon={!isOverlappingBreakoutsVisible && <JoinInnerIcon />}
        onClick={() => setIsOverlappingBreakoutsVisible((currentValue) => !currentValue)}
      >
        {isOverlappingBreakoutsVisible ? 'Show all breakouts' : 'Show only overlapping breakouts'}
      </ButtonV2>
    );
  };

  return (
    <>
      {isItemProcessing ? (
        <div className={styles['item-breakouts-loader']}>
          <Loader isCentered />
        </div>
      ) : (
        <div className={styles['item-breakouts-container']}>
          {itemBreakouts?.isNarrativesCompatible === false && (
            <Announcement
              callToAction={renderOverlappingBreakouts()}
              className={styles['item-breakouts-incompatible-group-banner']}
              data-testid="item-breakouts-incompatible-group-banner"
              header="This product group isn't available in prompts that require non-overlapping breakouts."
              text="Some Insights prompts require mutually exclusive breakouts to support their methodology. You can still use this group in other prompts. If you own the group, make it compatible by editing the overlapping breakout definitions so they no longer overlap."
              variant="info"
            />
          )}
          <div>
            {!itemBreakouts?.breakouts?.length && (
              <div className={styles['item-breakouts-add']}>
                <p>{`This ${groupType?.label} hasn't been defined yet.`}</p>
                {isOwner && (
                  <Button
                    data-testid="add-item-breakout"
                    onClick={() => handleNavigate({ id })}
                    text="Add a Breakout"
                    variant="secondary"
                  />
                )}
              </div>
            )}
            {itemBreakouts?.breakouts?.length > 0 && (
              <>
                <div className={styles['item-breakouts-search']}>
                  <Search
                    onChange={setValue}
                    placeholder="Search by breakout name or definitions"
                    value={value}
                  />
                  {isOwner && (
                    <Button
                      data-testid="create-item-breakout"
                      onClick={() => handleNavigate({ id })}
                      text="Add a Breakout"
                      variant="secondary"
                    />
                  )}
                </div>
                <div>
                  {currentItems?.map((breakout, breakoutIndex) => (
                    <Card key={breakoutIndex}>
                      <Breakout
                        breakout={breakout}
                        isOwner={isOwner}
                        itemsPage={itemsPage}
                        itemsRow={itemsRow}
                        onSubmit={onSubmit}
                        type={groupType?.type}
                        updateItemBreakouts={updateItemBreakouts}
                      />
                      {overlappingBreakoutTitles.has(breakout?.title) && (
                        <div
                          className={styles['item-breakout-overlap-message']}
                          data-testid="item-breakout-overlap-message"
                        >
                          <JoinInnerIcon />
                          <p>{`This breakout overlaps with others in this group: ${getOverlapMessage(
                            breakout?.title
                          )}`}</p>
                        </div>
                      )}
                    </Card>
                  ))}
                  <Pagination
                    onPageChange={onPageChange}
                    pageCount={pageCount}
                    show={visibleBreakouts?.length > itemsPerPage}
                  />
                </div>
                {visibleBreakouts && visibleBreakouts.length === 0 && (
                  <div className={styles['item-breakouts-search-empty-message']}>
                    <h3>No results found.</h3>
                    <p>Try refining your search.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

ItemBreakouts.propTypes = {
  groupType: PropTypes.object,
  item: PropTypes.shape({
    breakouts: PropTypes.array,
    id: PropTypes.number.isRequired,
    isNarrativesCompatible: PropTypes.bool,
    isOwner: PropTypes.bool,
    status: PropTypes.number,
  }).isRequired,
  itemsPage: PropTypes.number,
  itemsRow: PropTypes.string,
  onSubmit: PropTypes.func,
  readItem: PropTypes.func,
  updateItemBreakouts: PropTypes.func,
};

export default ItemBreakouts;
